import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import type { User } from '../../types/schema';
import { DAILY_BRIEFING_AUTO_DURATION_MS } from '../../utils/constants';
import { CustomModal } from '../CustomModal';
import { Btn, IconCheckCircle, IconClose, IconPlay, TEIQspinner } from '../ui';
import { useUserContext } from '../../contexts/UserContext';
import type { BriefingTopic } from './dailyBriefingData';
import {
    getConfirmationStats,
    getTodayDateKey,
    isPdfBriefingTopic,
    isVideoBriefingTopic,
    markBriefingTopicViewed,
    resolveBriefingPdfInlineUrl,
    resolveTopicUrl,
    revokeBriefingPdfInlineUrl,
} from './dailyBriefingData';

interface LaunchDisplayModalProps {
    visible: boolean;
    topic: BriefingTopic | null;
    onDismiss: () => void;
    /** Called after a successful mark with the updated company user directory (for confirmation stats). */
    onMarkedAsViewed?: (updatedUsers: User[]) => void | Promise<void>;
    /**
     * Home auto-launch: attempt video autoplay, auto-close after `autoCloseMs`,
     * and mark as viewed on timer or dismiss.
     */
    autoMode?: boolean;
    /** When true (Home), enter fullscreen as soon as media is ready. */
    autoFullscreen?: boolean;
    autoCloseMs?: number;
    /** Fired once when an autoMode session finishes (timer or dismiss), before/with close. */
    onAutoSessionComplete?: () => void;
}

function formatClock(totalSeconds: number): string {
    const safe = Math.max(0, Math.floor(totalSeconds));
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatMinutesLabel(totalSeconds: number): string {
    const minutes = Math.max(1, Math.round(totalSeconds / 60));
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

type VendorFullscreenDocument = Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
};

type VendorFullscreenElement = HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
};

function getFullscreenElement(): Element | null {
    const doc = document as VendorFullscreenDocument;
    return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

async function requestElementFullscreen(el: HTMLElement): Promise<void> {
    const vendorEl = el as VendorFullscreenElement;
    if (typeof el.requestFullscreen === 'function') {
        await el.requestFullscreen();
        return;
    }
    if (typeof vendorEl.webkitRequestFullscreen === 'function') {
        await vendorEl.webkitRequestFullscreen();
        return;
    }
    throw new Error('Fullscreen is not supported in this browser.');
}

async function exitDocumentFullscreen(): Promise<void> {
    const doc = document as VendorFullscreenDocument;
    if (getFullscreenElement() == null) return;
    if (typeof document.exitFullscreen === 'function') {
        await document.exitFullscreen();
        return;
    }
    if (typeof doc.webkitExitFullscreen === 'function') {
        await doc.webkitExitFullscreen();
    }
}

export function LaunchDisplayModal({
    visible,
    topic,
    onDismiss,
    onMarkedAsViewed,
    autoMode = false,
    autoFullscreen = false,
    autoCloseMs = DAILY_BRIEFING_AUTO_DURATION_MS,
    onAutoSessionComplete,
}: LaunchDisplayModalProps) {
    const { companyUser, setCompanyUser, setAllCompanyUsers, allCompanyUsers } = useUserContext();
    const videoRef = useRef<HTMLVideoElement>(null);
    const stageRef = useRef<HTMLDivElement>(null);
    const autoSessionDoneRef = useRef(false);
    /** CSS fullscreen fallback when the browser Fullscreen API is blocked (no user gesture). */
    const cssFullscreenFallbackRef = useRef(false);
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
    const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [mediaError, setMediaError] = useState<string | null>(null);
    /** Once true, keep native controls mounted — seeking briefly pauses and must not hide them. */
    const [hasStartedPlayback, setHasStartedPlayback] = useState(false);
    const [durationSec, setDurationSec] = useState<number | null>(null);
    const [isStageFullscreen, setIsStageFullscreen] = useState(false);
    const [markingViewed, setMarkingViewed] = useState(false);
    const [markError, setMarkError] = useState<string | null>(null);

    const alreadyViewedToday = Boolean(
        topic &&
            companyUser &&
            getConfirmationStats([companyUser], topic.id, getTodayDateKey()).confirmedCount > 0,
    );

    const clearPdfObjectUrl = useCallback((url: string | null) => {
        revokeBriefingPdfInlineUrl(url);
        setPdfObjectUrl(null);
    }, []);

    const resetMediaState = useCallback(() => {
        setDownloadUrl(null);
        setError(null);
        setMediaError(null);
        setHasStartedPlayback(false);
        setDurationSec(null);
        setIsStageFullscreen(false);
        setLoading(false);
        setMarkingViewed(false);
        setMarkError(null);
    }, []);

    const finishDismiss = useCallback(() => {
        cssFullscreenFallbackRef.current = false;
        void exitDocumentFullscreen().catch(() => undefined);
        clearPdfObjectUrl(pdfObjectUrl);
        resetMediaState();
        onDismiss();
    }, [clearPdfObjectUrl, onDismiss, pdfObjectUrl, resetMediaState]);

    const persistMarkAsViewed = useCallback(async (): Promise<boolean> => {
        if (!topic) return false;
        if (!companyUser?.id) return false;

        const directoryUser = allCompanyUsers.find((u) => u.id === companyUser.id);
        const { nextLessons, alreadyViewed } = await markBriefingTopicViewed({
            userId: companyUser.id,
            topic,
            existingSafetyLessons: directoryUser?.safetyLessons ?? companyUser.safetyLessons,
        });
        if (alreadyViewed) return true;

        const serialized = JSON.stringify(nextLessons);
        setCompanyUser({ ...companyUser, safetyLessons: serialized });
        const updatedUsers = allCompanyUsers.map((user) =>
            user.id === companyUser.id ? { ...user, safetyLessons: serialized } : user,
        );
        setAllCompanyUsers(updatedUsers);
        await onMarkedAsViewed?.(updatedUsers);
        return true;
    }, [
        allCompanyUsers,
        companyUser,
        onMarkedAsViewed,
        setAllCompanyUsers,
        setCompanyUser,
        topic,
    ]);

    const handleMarkAsViewed = useCallback(async () => {
        if (!topic) return;
        if (!companyUser?.id) {
            setMarkError('Sign in as a company user to mark this topic as viewed.');
            return;
        }
        if (markingViewed) return;

        setMarkingViewed(true);
        setMarkError(null);
        try {
            await persistMarkAsViewed();
            finishDismiss();
        } catch (err) {
            console.error('Failed to mark briefing topic as viewed', err);
            Sentry.captureException(err);
            setMarkError('Could not save confirmation. Please try again.');
            setMarkingViewed(false);
        }
    }, [companyUser?.id, finishDismiss, markingViewed, persistMarkAsViewed, topic]);

    const completeAutoSession = useCallback(async () => {
        if (!autoMode || autoSessionDoneRef.current) return;
        autoSessionDoneRef.current = true;
        setMarkingViewed(true);
        try {
            if (topic && companyUser?.id && !alreadyViewedToday) {
                try {
                    await persistMarkAsViewed();
                } catch (err) {
                    console.error('Failed to mark briefing topic as viewed (auto)', err);
                    Sentry.captureException(err);
                }
            }
        } finally {
            onAutoSessionComplete?.();
            finishDismiss();
        }
    }, [
        alreadyViewedToday,
        autoMode,
        companyUser?.id,
        finishDismiss,
        onAutoSessionComplete,
        persistMarkAsViewed,
        topic,
    ]);

    const handleDismiss = useCallback(() => {
        if (autoMode) {
            void completeAutoSession();
            return;
        }
        finishDismiss();
    }, [autoMode, completeAutoSession, finishDismiss]);

    useEffect(() => {
        if (visible && autoMode) {
            autoSessionDoneRef.current = false;
        }
    }, [autoMode, topic?.id, visible]);

    useEffect(() => {
        let cancelled = false;
        let localPdfUrl: string | null = null;

        if (!visible || !topic) {
            clearPdfObjectUrl(pdfObjectUrl);
            resetMediaState();
            return;
        }

        setLoading(true);
        setError(null);
        setMediaError(null);
        setHasStartedPlayback(false);
        setDurationSec(null);
        setIsStageFullscreen(false);
        setDownloadUrl(null);
        clearPdfObjectUrl(pdfObjectUrl);

        (async () => {
            const resolved = await resolveTopicUrl(topic.contentPath);
            if (cancelled) return;
            if (!resolved) {
                setError('Unable to load this topic’s file.');
                setLoading(false);
                return;
            }

            setDownloadUrl(resolved);

            if (isPdfBriefingTopic(topic)) {
                localPdfUrl = await resolveBriefingPdfInlineUrl(topic.contentPath);
                if (cancelled) {
                    revokeBriefingPdfInlineUrl(localPdfUrl);
                    return;
                }
                if (!localPdfUrl) {
                    setError('Unable to display this PDF in the viewer.');
                } else {
                    setPdfObjectUrl(localPdfUrl);
                }
            }

            setLoading(false);
        })();

        return () => {
            cancelled = true;
            revokeBriefingPdfInlineUrl(localPdfUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- pdfObjectUrl cleared via functional flow on visible/topic change
    }, [topic, visible]);

    useEffect(() => {
        const syncFullscreen = () => {
            const active = getFullscreenElement();
            if (active && active === stageRef.current) {
                cssFullscreenFallbackRef.current = false;
                setIsStageFullscreen(true);
                return;
            }
            if (cssFullscreenFallbackRef.current) {
                setIsStageFullscreen(true);
                return;
            }
            setIsStageFullscreen(false);
        };

        document.addEventListener('fullscreenchange', syncFullscreen);
        document.addEventListener('webkitfullscreenchange', syncFullscreen);
        return () => {
            document.removeEventListener('fullscreenchange', syncFullscreen);
            document.removeEventListener('webkitfullscreenchange', syncFullscreen);
        };
    }, []);

    useEffect(() => {
        if (visible) return;
        cssFullscreenFallbackRef.current = false;
        void exitDocumentFullscreen().catch(() => undefined);
    }, [visible]);

    // Auto-mode: close after fixed duration.
    useEffect(() => {
        if (!visible || !autoMode || !topic) return;
        const timerId = window.setTimeout(() => {
            void completeAutoSession();
        }, autoCloseMs);
        return () => window.clearTimeout(timerId);
    }, [autoCloseMs, autoMode, completeAutoSession, topic, visible]);

    const isPdf = topic ? isPdfBriefingTopic(topic) : false;
    const isVideo = topic ? isVideoBriefingTopic(topic) : false;
    const isPoster = Boolean(topic && !isPdf && !isVideo);
    const mediaReadyForFullscreen = Boolean(
        (isPdf && pdfObjectUrl) || (isPoster && downloadUrl) || (isVideo && downloadUrl),
    );
    const canFullscreenStage = mediaReadyForFullscreen;

    // Home auto-launch: enter fullscreen as soon as media is ready.
    useEffect(() => {
        if (!visible || !autoFullscreen || !mediaReadyForFullscreen || loading) return;
        const stage = stageRef.current;
        if (!stage) return;

        let cancelled = false;
        (async () => {
            try {
                await requestElementFullscreen(stage);
                if (!cancelled) {
                    cssFullscreenFallbackRef.current = false;
                    setIsStageFullscreen(true);
                }
            } catch {
                // Browsers often block Fullscreen API without a user gesture — use CSS cover.
                if (!cancelled) {
                    cssFullscreenFallbackRef.current = true;
                    setIsStageFullscreen(true);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [autoFullscreen, downloadUrl, loading, mediaReadyForFullscreen, pdfObjectUrl, visible]);

    // Auto-mode: start playback ASAP for the Home 25s window (muted fallback if needed).
    useEffect(() => {
        if (!visible || !autoMode || !isVideo || !downloadUrl || loading) return;

        let cancelled = false;
        let el = videoRef.current;
        let playAttempted = false;

        const tryPlay = async () => {
            el = videoRef.current;
            if (cancelled || !el || playAttempted) return;
            playAttempted = true;
            el.playsInline = true;
            try {
                el.muted = false;
                await el.play();
                return;
            } catch {
                // Autoplay with sound is often blocked — muted usually still works.
            }
            try {
                el.muted = true;
                el.defaultMuted = true;
                await el.play();
            } catch {
                playAttempted = false;
                if (!cancelled) {
                    setMediaError('Unable to autoplay. Use the video controls to play.');
                }
            }
        };

        const onCanPlay = () => {
            void tryPlay();
        };

        // Element may mount one tick after downloadUrl is set.
        const startId = window.setTimeout(() => {
            el = videoRef.current;
            if (!el || cancelled) return;
            el.addEventListener('canplay', onCanPlay);
            el.addEventListener('loadeddata', onCanPlay);
            if (el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                void tryPlay();
            } else {
                el.load();
            }
        }, 0);

        return () => {
            cancelled = true;
            window.clearTimeout(startId);
            el?.removeEventListener('canplay', onCanPlay);
            el?.removeEventListener('loadeddata', onCanPlay);
        };
    }, [autoMode, downloadUrl, isVideo, loading, visible]);

    const handlePlayClick = () => {
        const el = videoRef.current;
        if (!el) return;
        const playAttempt = el.play();
        if (playAttempt && typeof playAttempt.catch === 'function') {
            playAttempt.catch(() => {
                setMediaError('Unable to autoplay. Use the video controls to play.');
            });
        }
    };

    const handleToggleFullscreen = async () => {
        if (!canFullscreenStage || !stageRef.current) return;
        try {
            if (isStageFullscreen) {
                cssFullscreenFallbackRef.current = false;
                await exitDocumentFullscreen();
                setIsStageFullscreen(false);
                return;
            }
            await requestElementFullscreen(stageRef.current);
        } catch {
            setMediaError(
                'Fullscreen is unavailable in this browser. Use “Open in a new tab” instead.',
            );
        }
    };

    const modalHeader = topic?.title ?? 'Launch & Display';
    const topicId = topic?.displayId ?? '—';
    const category = topic?.folderName?.trim() || '—';
    const styleLabel = topic?.style ?? '—';
    const timeValue = durationSec != null ? formatMinutesLabel(durationSec) : '—';
    const stageCaption =
        durationSec != null ? `${topicId} · ${formatClock(durationSec)}` : topicId;
    const openInNewTabLabel = isVideo
        ? 'Open video in a new tab'
        : isPdf
          ? 'Open PDF in a new tab'
          : 'Open poster in a new tab';
    const pdfViewerSrc = pdfObjectUrl
        ? `${pdfObjectUrl.split('#')[0]}${isStageFullscreen ? '#zoom=100' : ''}`
        : null;

    return (
        <CustomModal
            visible={visible}
            onDismiss={handleDismiss}
            header={modalHeader}
            size="default"
            overlayClassName="db-launch-modal-overlay"
            contentClassName="db-launch-modal-panel"
            bodyClassName="db-launch-modal-body"
            footer={
                <div className="db-launch-footer-actions">
                    {markError ? (
                        <p className="db-modal-error db-launch-mark-error" role="alert">
                            {markError}
                        </p>
                    ) : null}
                    <Btn
                        variant="secondary"
                        buttonText="Close"
                        buttonOnClick={handleDismiss}
                        buttonDisabled={markingViewed}
                    />
                    {!autoMode ? (
                        <Btn
                            variant="primary"
                            buttonText={
                                markingViewed
                                    ? 'Saving…'
                                    : alreadyViewedToday
                                      ? 'Viewed'
                                      : 'Mark as Viewed'
                            }
                            buttonIcon={<IconCheckCircle />}
                            buttonOnClick={() => {
                                void handleMarkAsViewed();
                            }}
                            buttonDisabled={
                                markingViewed || alreadyViewedToday || !topic || !companyUser?.id
                            }
                            buttonTitle={
                                !companyUser?.id
                                    ? 'Sign in as a company user to mark this topic as viewed'
                                    : alreadyViewedToday
                                      ? 'Already marked as viewed today'
                                      : undefined
                            }
                        />
                    ) : null}
                </div>
            }
        >
            {!topic ? (
                <p>No topic selected.</p>
            ) : loading ? (
                <div className="db-modal-loading">
                    <TEIQspinner />
                </div>
            ) : error ? (
                <div className="db-modal-error" role="alert">
                    <p>{error}</p>
                    {downloadUrl && (
                        <p className="db-launch-fallback">
                            <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                                {openInNewTabLabel}
                            </a>
                        </p>
                    )}
                </div>
            ) : (
                <div className="db-launch-body">
                    <div
                        ref={stageRef}
                        className={[
                            'db-launch-stage',
                            isVideo ? 'db-launch-stage--video' : '',
                            isPdf ? 'db-launch-stage--pdf' : '',
                            isPoster ? 'db-launch-stage--poster' : '',
                            isStageFullscreen ? 'is-fullscreen' : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    >
                        {isVideo && downloadUrl ? (
                            <>
                                <video
                                    key={downloadUrl}
                                    ref={videoRef}
                                    className="db-launch-media"
                                    src={downloadUrl}
                                    controls={hasStartedPlayback}
                                    playsInline
                                    autoPlay={autoMode}
                                    preload={autoMode ? 'auto' : 'metadata'}
                                    onPlay={() => setHasStartedPlayback(true)}
                                    onLoadedMetadata={(event) => {
                                        const duration = event.currentTarget.duration;
                                        if (Number.isFinite(duration) && duration > 0) {
                                            setDurationSec(duration);
                                        }
                                    }}
                                    onError={() => {
                                        setMediaError(
                                            'Unable to play this video in the viewer. Try opening it in a new tab.',
                                        );
                                    }}
                                >
                                    Your browser does not support video playback.
                                </video>
                                {!hasStartedPlayback && (!autoMode || mediaError) ? (
                                    <button
                                        type="button"
                                        className="db-launch-play"
                                        aria-label={`Play ${topic.title}`}
                                        onClick={handlePlayClick}
                                    >
                                        <IconPlay iconClasses="db-launch-play-icon" />
                                    </button>
                                ) : null}
                                <span
                                    className={[
                                        'db-launch-stage-caption',
                                        hasStartedPlayback ? 'db-launch-stage-caption--with-controls' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                >
                                    {stageCaption}
                                </span>
                            </>
                        ) : isPdf && pdfViewerSrc ? (
                            <object
                                key={pdfViewerSrc}
                                className="db-launch-frame db-launch-pdf-object"
                                title={topic.title}
                                data={pdfViewerSrc}
                                type="application/pdf"
                            >
                                <iframe
                                    className="db-launch-frame"
                                    title={topic.title}
                                    src={pdfViewerSrc}
                                />
                            </object>
                        ) : downloadUrl ? (
                            <img className="db-launch-image" src={downloadUrl} alt={topic.title} />
                        ) : null}

                        {(canFullscreenStage && !autoFullscreen) || isStageFullscreen ? (
                            <div className="db-launch-stage-toolbar">
                                {canFullscreenStage && !autoFullscreen ? (
                                    <Btn
                                        variant="secondary"
                                        buttonClasses="db-launch-fullscreen-btn"
                                        buttonText={
                                            isStageFullscreen ? 'Exit fullscreen' : 'View fullscreen'
                                        }
                                        buttonOnClick={() => {
                                            void handleToggleFullscreen();
                                        }}
                                    />
                                ) : null}
                                {isStageFullscreen ? (
                                    <Btn
                                        variant="secondary"
                                        buttonClasses="db-launch-fullscreen-btn db-launch-close-btn"
                                        buttonText="Close"
                                        buttonIcon={<IconClose />}
                                        ariaLabel="Close briefing"
                                        buttonOnClick={handleDismiss}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </div>

                    {mediaError ? (
                        <p className="db-modal-error" role="alert">
                            {mediaError}
                        </p>
                    ) : null}

                    {downloadUrl ? (
                        <p className="db-launch-fallback">
                            <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                                {openInNewTabLabel}
                            </a>
                            {' '}
                            if it doesn&apos;t load here.
                        </p>
                    ) : null}

                    <dl className="db-launch-meta-grid">
                        <div className="db-launch-meta-card">
                            <dt>Topic ID</dt>
                            <dd className="db-launch-meta-mono">{topicId}</dd>
                        </div>
                        {isVideo ? (
                            <div className="db-launch-meta-card">
                                <dt>Time Value</dt>
                                <dd className="db-launch-meta-mono">{timeValue}</dd>
                            </div>
                        ) : null}
                        <div className="db-launch-meta-card">
                            <dt>Category</dt>
                            <dd>{category}</dd>
                        </div>
                        <div className="db-launch-meta-card">
                            <dt>Style</dt>
                            <dd>{styleLabel}</dd>
                        </div>
                    </dl>
                </div>
            )}
        </CustomModal>
    );
}
